// FTEDN — Auth state management (Zustand)
import { create } from 'zustand';
import { isStaffRole, type AuthenticatedUser } from '@ftedn/shared';
import { ApiClientError, apiClient } from '../hooks/useApi';
import {
  auth,
  onIdTokenChanged,
  signInWithCustomToken,
  signOut,
} from '../services/firebase';

interface AuthState {
  token: string | null;
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: (idToken: string) => Promise<AuthResult>;
  signup: () => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => () => void;
}

export type AuthResult =
  | { success: true }
  | { success: false; errorCode?: string; message?: string };

function extractAuthError(error: unknown): AuthResult {
  if (error instanceof ApiClientError) {
    return {
      success: false,
      errorCode: error.code,
      message: error.message,
    };
  }

  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return { success: false };
  }

  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return { success: false };
  }

  const data = (response as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) {
    return { success: false };
  }

  const apiError = (data as { error?: unknown }).error;
  if (typeof apiError !== 'object' || apiError === null) {
    return { success: false };
  }

  const errorRecord = apiError as { code?: unknown; message?: unknown };
  return {
    success: false,
    errorCode: typeof errorRecord.code === 'string' ? errorRecord.code : undefined,
    message: typeof errorRecord.message === 'string' ? errorRecord.message : undefined,
  };
}

async function hydrateSession() {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return null;

  const token = await firebaseUser.getIdToken();
  localStorage.setItem('ftedn_token', token);

  try {
    const response = await apiClient.get('/auth/me');
    const user = response.data?.user as AuthenticatedUser | undefined;
    if (!user || !isStaffRole(user.role)) {
      localStorage.removeItem('ftedn_token');
      return null;
    }

    return {
      token,
      user,
    };
  } catch {
    localStorage.removeItem('ftedn_token');
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const customToken = response.data?.customToken as string | undefined;
      if (!customToken) return false;

      await signInWithCustomToken(auth, customToken);
      const token = await auth.currentUser!.getIdToken(true);
      const user = response.data?.user as AuthenticatedUser | undefined;
      if (!user || !isStaffRole(user.role)) {
        localStorage.removeItem('ftedn_token');
        return false;
      }

      localStorage.setItem('ftedn_token', token);

      set({
        token,
        user,
        isAuthenticated: true,
      });
      return true;
    } catch {
      return false;
    }
  },

  loginWithGoogle: async (idToken: string) => {
    try {
      const response = await apiClient.post('/auth/google', { idToken });
      const customToken = response.data?.customToken as string | undefined;
      if (!customToken) return { success: false };

      await signInWithCustomToken(auth, customToken);
      const token = await auth.currentUser!.getIdToken(true);
      const user = response.data?.user as AuthenticatedUser | undefined;
      if (!user || !isStaffRole(user.role)) {
        localStorage.removeItem('ftedn_token');
        return { success: false };
      }

      localStorage.setItem('ftedn_token', token);

      set({
        token,
        user,
        isAuthenticated: true,
      });
      return { success: true };
    } catch (error) {
      return extractAuthError(error);
    }
  },

  signup: async () => false,

  logout: async () => {
    await signOut(auth);
    localStorage.removeItem('ftedn_token');
    set({ token: null, user: null, isAuthenticated: false });
  },

  checkAuth: () => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        localStorage.removeItem('ftedn_token');
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      set({ isLoading: true });

      const session = await hydrateSession();
      set({
        token: session?.token ?? null,
        user: session?.user ?? null,
        isAuthenticated: Boolean(session?.token),
        isLoading: false,
      });
    });

    return unsubscribe;
  },
}));
